import React, { useState, Fragment, useRef, useEffect } from 'react';
import { RouteComponentProps, navigate, Link } from '@reach/router';
import LogoSmDark from 'assets/logo-neuvana-dark-x1.png';
import Input from 'components/Form/Input';
import { RFC } from 'state/StateTypes';
import styled from 'styled-components';
import Checkmark from 'components/Icons/Checkmark';
import Button from 'components/Button';
import SecondaryButton from 'components/SecondaryButton';
import ErrorMessage from 'components/ErrorMessage';
import Form from 'components/Form';
import Container from 'components/Container';
import FormContainer from 'components/FormContainer';
import Header from 'components/Header';
import Instructions from 'components/Instructions';
import { forgotPassword } from 'state/user/UserActions';
import { InputChange, FormSubmission, Error, ErrorCodes } from 'types';
import { validateEmail, presentErrorBasedOnCode } from 'utils';

const ForgotPassword: RFC<RouteComponentProps> = () => {
   const [email, setEmail] = useState<string>('');
   const [verificationSent, setVerificationSent] = useState<boolean>(false);
   const [loading, setLoading] = useState<boolean>(false);
   const forgotPasswordInput = useRef<HTMLInputElement>(null);
   const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
   const [error, setError] = useState<Error | null>(null);

   useEffect(() => {
      if (forgotPasswordInput && forgotPasswordInput.current) {
         forgotPasswordInput.current.focus();
      }
   }, []);

   const handleSubmit = async (e: FormSubmission): Promise<void> => {
      e.preventDefault();
      try {
         setLoading(true);
         await forgotPassword({ username: email });
      } catch (error) {
         //if error results from missing token it will not have a json method
         error = error.json ? await error.json() : error;
         setError(error);
      }
      !error && setVerificationSent(true);
      setLoading(false);
   };

   useEffect(() => {
      setIsValidEmail(validateEmail(email));
   }, [email]);
   return (
      <Container margin="0 auto">
         <Header>
            <img src={LogoSmDark} alt="Neuvana small logo" height={150} width={150} />
         </Header>
         <FormContainer>
            {!verificationSent ? (
               <>
                  <ResetPasswordHeader>Reset Password</ResetPasswordHeader>
                  <Instructions>Please enter the email associated with your account</Instructions>
                  <Form onSubmit={handleSubmit}>
                     <Input
                        label="Email"
                        value={email}
                        name="username"
                        onChange={(e: InputChange) => setEmail(e.target.value)}
                        size="full"
                        errorMessage="Email is required"
                        type="email"
                        required={true}
                        ref={forgotPasswordInput}
                     />
                     <Button isLoading={loading} disabled={!isValidEmail || loading}>
                        SUBMIT
                     </Button>
                     <SecondaryButton color="red" onClick={() => navigate('/')}>
                        CANCEL
                     </SecondaryButton>
                  </Form>
               </>
            ) : (
               <>
                  {error ? (
                     <>
                        <ErrorMessage>
                           {error.code === ErrorCodes.NO_RESULTS_ERROR
                              ? 'There is not an account associated with the email you provided'
                              : presentErrorBasedOnCode(error.code as ErrorCodes)}
                        </ErrorMessage>
                        <TryAgainButton
                           onClick={() => {
                              setError(null);
                              setVerificationSent(false);
                              setEmail('');
                           }}>
                           Try another email
                        </TryAgainButton>
                     </>
                  ) : (
                     <>
                        <CheckmarkContainer>
                           <Checkmark />
                        </CheckmarkContainer>
                        <Instructions>
                           Your request has been submitted. You should receive an email shortly, please use the link in that email to change your
                           password.
                        </Instructions>
                        <ReturnToLogin to="/">Return to login</ReturnToLogin>
                     </>
                  )}
               </>
            )}
         </FormContainer>
      </Container>
   );
};

export default ForgotPassword;

const ResetPasswordHeader = styled.h2`
   color: ${({ theme }) => theme.primaryBlueDark};
   font-weight: bold;
   display: block;
   margin: 0 auto 1.5em auto;
`;

const CheckmarkContainer = styled.div`
   margin: 0 auto 1em auto;
   text-align: center;
`;

const ReturnToLogin = styled(Link)`
   margin: 0 auto;
   color: ${({ theme }) => theme.secondaryBlueLight};
   cursor: pointer;
   font-weight: 600;
   text-align: center;

   &:hover {
      color: ${({ theme }) => theme.secondaryBlueDark};
   }
`;

const TryAgainButton = styled.button.attrs({ type: 'button' })`
   margin: 1em auto;
   padding: 6px 8px;
   border-radius: 4px;
   background-color: ${({ theme }) => theme.secondaryBlueLight};
   color: ${({ theme }) => theme.primaryWhite};
   font-size: 1.2em;
   font-weight: bold;
   border: none;
   cursor: pointer;
   &:hover {
      background-color: ${({ theme }) => theme.secondaryBlueDark};
   }
`;
